/**
 * The decision, on the Approval node's own box: a red | green toggle shown
 * while the run waits at this node. Approve resumes the run; Reject asks for a
 * reason inline (the server refuses a rejection without one) and stops it.
 * Same endpoint and rules as the Approvals inbox — `can_decide` and its reason
 * come from the server.
 *
 * `nodrag nopan` keep clicks on the controls from dragging the node or panning
 * the canvas.
 */

import { useState } from "react";
import { Button, Group, Stack, Text, TextInput, UnstyledButton } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { decideApproval, type ApprovalOut } from "@/shared/api/approvals";
import { toastApiError } from "@/shared/ui/toast";

const GREEN = "var(--mantine-color-green-6)";
const RED = "var(--mantine-color-red-6)";

function Half({
  label,
  icon,
  color,
  active,
  disabled,
  onClick,
  testId,
}: {
  label: string;
  icon: React.ReactNode;
  color: string;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <UnstyledButton
      className="nodrag nopan"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      disabled={disabled}
      data-testid={testId}
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        padding: "4px 8px",
        fontSize: 12,
        fontWeight: 700,
        color: active ? "#fff" : color,
        background: active ? color : "transparent",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {icon}
      {label}
    </UnstyledButton>
  );
}

export function ApprovalToggle({
  approval,
  onDecided,
}: {
  approval: ApprovalOut;
  onDecided: () => void;
}) {
  const getToken = useAccessToken();
  const [rejecting, setRejecting] = useState(false);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const decided = approval.status !== "pending";
  const approved = approval.status === "approved";
  const canDecide = approval.can_decide && !decided;

  const decide = async (yes: boolean) => {
    setBusy(true);
    try {
      await decideApproval(await getToken(), approval.approval_id, {
        approved: yes,
        comment: comment.trim(),
      });
      setRejecting(false);
      onDecided();
    } catch (err) {
      toastApiError(yes ? "Could not approve" : "Could not reject", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap={4} mt={6} className="nodrag nopan" data-testid="approval-toggle">
      {approval.title && !decided && (
        <Text size="xs" c="dimmed" lineClamp={2}>
          {approval.title}
        </Text>
      )}
      <Group
        gap={0}
        wrap="nowrap"
        style={{
          border: `1px solid ${decided ? (approved ? GREEN : RED) : "var(--mantine-color-gray-4)"}`,
          borderRadius: 999,
          overflow: "hidden",
        }}
      >
        <Half
          label={decided && !approved ? "Rejected" : "Reject"}
          icon={<IconX size={12} />}
          color={RED}
          active={(decided && !approved) || rejecting}
          disabled={!canDecide || busy}
          onClick={() => setRejecting(true)}
          testId="approval-reject"
        />
        <Half
          label={approved ? "Approved" : "Approve"}
          icon={<IconCheck size={12} />}
          color={GREEN}
          active={approved}
          disabled={!canDecide || busy}
          onClick={() => void decide(true)}
          testId="approval-approve"
        />
      </Group>
      {rejecting && canDecide && (
        <Group gap={4} wrap="nowrap">
          <TextInput
            className="nodrag nopan"
            size="xs"
            placeholder="Why? (required)"
            value={comment}
            onChange={(e) => setComment(e.currentTarget.value)}
            onKeyDown={(e) => e.stopPropagation()}
            style={{ flex: 1 }}
            autoFocus
          />
          <Button
            className="nodrag nopan"
            size="compact-xs"
            color="red"
            disabled={comment.trim().length < 3 || busy}
            loading={busy}
            onClick={(e) => {
              e.stopPropagation();
              void decide(false);
            }}
          >
            Reject
          </Button>
        </Group>
      )}
      {!approval.can_decide && !decided && approval.cannot_decide_reason && (
        <Text size="xs" c="dimmed">
          {approval.cannot_decide_reason}
        </Text>
      )}
    </Stack>
  );
}
